"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Shield, Network, UserCog, Briefcase, Bell, Link as LinkIcon, Calendar, Zap, Cpu, Activity, CircleCheck, Terminal } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inboundAgents = [{
  id: "AGT-IN-01",
  name: "Shield",
  role: "Overflow Protection",
  icon: Shield,
  status: "ONLINE",
  directives: ["Eliminate hold times", "Capture 100% of missed calls", "Instant caller engagement"]
}, {
  id: "AGT-IN-02",
  name: "Bridge",
  role: "Smart Routing",
  icon: Network,
  status: "ONLINE",
  directives: ["Analyze caller intent", "Precision department routing", "Minimize transfer friction"]
}, {
  id: "AGT-IN-03",
  name: "Concierge",
  role: "Reception & Booking",
  icon: UserCog,
  status: "ACTIVE",
  directives: ["24/7 Front desk management", "Natural language scheduling", "Calendar synchronization"]
}, {
  id: "AGT-IN-04",
  name: "Closer",
  role: "Sales & Technical",
  icon: Briefcase,
  status: "STANDBY",
  directives: ["Technical query resolution", "Lead qualification logic", "Conversion optimization"]
}];
const outboundAgents = [{
  id: "AGT-OUT-01",
  name: "Engage",
  role: "Retention & Reminders",
  icon: Bell,
  status: "ONLINE",
  directives: ["Automated appointment reminders", "Churn prevention protocols", "Rescheduling automation"]
}, {
  id: "AGT-OUT-02",
  name: "Connect",
  role: "Lead Qualification",
  icon: LinkIcon,
  status: "ACTIVE",
  directives: ["High-volume lead filtering", "Interest verification", "Instant live transfers"]
}, {
  id: "AGT-OUT-03",
  name: "Advance",
  role: "Proactive Scheduling",
  icon: Calendar,
  status: "ONLINE",
  directives: ["Calendar gap filling", "Seasonal campaign outreach", "Reactivation sequences"]
}, {
  id: "AGT-OUT-04",
  name: "Convert",
  role: "Autonomous Sales",
  icon: Zap,
  status: "DEPLOYED",
  directives: ["Cold outreach execution", "Value proposition delivery", "Meeting security"]
}];
export default function ModularAiAgents() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="modular-ai-agents" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(80,70,229,0.03)_0%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className={`transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-mono mb-6">
              <Cpu className="size-3" />
              <span>SYSTEM ARCHITECTURE</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
              Modular AI Agent <span className="text-primary">Selection</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Deploy specialized intelligence units for every stage of your customer journey. 
              Select from our roster of pre-trained, industry-specific AI agents.
            </p>
          </div>

          <Tabs defaultValue="inbound" className="w-full max-w-6xl mx-auto">
            <div className="flex justify-center mb-12">
              <TabsList className="bg-secondary/50 border border-border p-1 h-auto rounded-full">
                <TabsTrigger value="inbound" className="rounded-full px-8 py-3 text-base data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-300">
                  Inbound Agents
                </TabsTrigger>
                <TabsTrigger value="outbound" className="rounded-full px-8 py-3 text-base data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-300">
                  Outbound Agents
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="inbound" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {inboundAgents.map((agent, i) => <AgentCard key={agent.id} agent={agent} index={i} />)}
              </div>
            </TabsContent>

            <TabsContent value="outbound" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {outboundAgents.map((agent, i) => <AgentCard key={agent.id} agent={agent} index={i} />)}
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-16 text-center">
            <Link to="/request-demo">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-xl shadow-[0_0_20px_rgba(80,70,229,0.3)] hover:shadow-[0_0_30px_rgba(80,70,229,0.5)] transition-all">
                Initialize Agent Deployment
                <Terminal className="ml-2 size-5" />
              </Button>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground font-mono">
              
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function AgentCard({
  agent,
  index
}: {
  agent: any;
  index: number;
}) {
  return <div data-index={index} className="group relative h-full">
      {}
      <div className="absolute -inset-0.5 bg-gradient-to-b from-primary/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
      
      <Card className="h-full bg-card/40 backdrop-blur-md border-border/50 hover:border-primary/50 transition-all duration-300 overflow-hidden flex flex-col relative">
        {}
        <div className="h-1 w-full bg-gradient-to-r from-primary/0 via-primary/50 to-primary/0 opacity-50" />
        
        <CardHeader className="pb-2 space-y-4">
          <div className="flex justify-between items-start">
            <div className="flex flex-col">
              <span className="text-[10px] font-mono text-muted-foreground tracking-widest mb-1">ID: {agent.id}</span>
              <div className="flex items-center gap-2">
                <div className={`size-2 rounded-full ${agent.status === 'ONLINE' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-amber-500'}`} />
                <span className="text-[10px] font-mono font-bold text-primary/80 tracking-wider">{agent.status}</span>
              </div>
            </div>
            <SafeHydrate><Avatar className="size-12 rounded-lg border border-primary/20 bg-primary/5 group-hover:bg-primary/10 transition-colors">
              <AvatarFallback className="bg-transparent text-primary">
                <agent.icon className="size-6" />
              </AvatarFallback>
            </Avatar></SafeHydrate>
          </div>
          
          <div>
            <CardTitle className="text-2xl font-bold tracking-tight text-white group-hover:text-primary transition-colors">
              {agent.name}
            </CardTitle>
            <CardDescription className="text-sm font-medium text-muted-foreground uppercase tracking-wider mt-1">
              {agent.role}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col justify-end pt-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-mono text-primary/70 border-b border-border/30 pb-2">
              <Activity className="size-3" />
              <span>PRIMARY DIRECTIVES</span>
            </div>
            <ul className="space-y-2">
              {agent.directives.map((directive: string, i: number) => <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground/90 font-mono leading-tight">
                  <CircleCheck className="size-3.5 mt-0.5 text-primary shrink-0" />
                  <span>{directive}</span>
                </li>)}
            </ul>
          </div>
          
          {}
          <div className="absolute bottom-0 right-0 p-2 opacity-20">
             <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 20V10L10 20H20Z" fill="currentColor" className="text-primary" />
             </svg>
          </div>
        </CardContent>
      </Card>
    </div>;
}